import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { MapPin, Home, Building2, Star, Plus, ArrowLeft, Check } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * CustomerAddressPicker — standalone modal that pops up when an existing
 * customer is selected on a job-creation form.
 *
 * Workflow:
 *   • Fetches /api/customers/{customer_id}/addresses on open.
 *   • If exactly 1 address → auto-picks it via `onPick`, never shows the
 *     modal (1b — user already confirmed this UX choice).
 *   • If 2+ addresses → shows the modal with a card per address + a
 *     "+ Add New Address" button at the bottom.
 *   • "+ Add New Address" expands an inline form with Google Places
 *     autocomplete (same /api/places/autocomplete endpoint the main
 *     address field uses) and the four canonical fields (address / city
 *     / state / zip_code) — names match SMS/email placeholders so
 *     templates don't need to change.
 *   • Saving a new address POSTs to the customer profile AND emits the
 *     saved row through `onPick`.
 *
 * Used by both JobFormDialog (residential field-service) and
 * StoreJobFormDialog (multi-location store) so the UX stays consistent.
 *
 * Props:
 *   • customer  — { customer_id, name, ... } | null
 *                  When set non-null AND `open=true`, the modal opens.
 *   • open      — controlled open boolean (parent toggles to false to dismiss).
 *   • onClose   — () => void — fired when user dismisses without picking.
 *   • onPick    — (address) => void — fired with the chosen address row;
 *                  parent should populate its job-form fields from the
 *                  address_line1 / city / state / zip_code values.
 */

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const getAuthHeaders = () => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const NewAddressForm = ({ customerId, onSaved, onCancel }) => {
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const handleAddressInput = (val) => {
    setAddress(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/places/autocomplete?input=${encodeURIComponent(val)}`, { headers: getAuthHeaders() });
        setSuggestions(r.data?.predictions || []);
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 300);
  };

  const pickSuggestion = async (s) => {
    setShowSuggestions(false);
    if (!s.place_id || s.place_id === "manual") {
      setAddress(s.description || "");
      return;
    }
    try {
      const r = await axios.get(`${API}/places/details/${s.place_id}`, { headers: getAuthHeaders() });
      const d = r.data || {};
      setAddress(d.street || d.formatted_address || s.description || "");
      if (d.city) setCity(d.city);
      if (d.state) setStateCode(d.state);
      if (d.zip) setZipCode(d.zip);
    } catch { setAddress(s.description || ""); }
  };

  const handleSave = async () => {
    const street = address.trim();
    if (!street) { toast.error("Address is required"); return; }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/customers/${customerId}/addresses`,
        { address: street, city: city.trim(), state: stateCode.trim(), zip_code: zipCode.trim() },
        { headers: getAuthHeaders() },
      );
      const saved = r.data?.address;
      if (saved) {
        toast.success("Address saved to customer profile");
        onSaved(saved);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save address");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 pb-1" data-testid="picker-new-address-form">
      <div className="relative">
        <label className="text-[11px] font-medium text-muted-foreground">Address</label>
        <Input
          type="text"
          value={address}
          onChange={(e) => handleAddressInput(e.target.value)}
          placeholder="Start typing the street address…"
          className="mt-0.5"
          data-testid="picker-new-address-street-input"
          autoFocus
        />
        {showSuggestions && (suggestions.length > 0 || loading) && (
          <div className="absolute z-10 left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto overscroll-contain touch-pan-y" data-testid="picker-new-address-suggestions">
            {loading && <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>}
            {suggestions.map((s, i) => (
              <button
                key={s.place_id || i}
                type="button"
                onClick={() => pickSuggestion(s)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors"
                data-testid={`picker-new-address-suggestion-${i}`}
              >
                {s.description}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] font-medium text-muted-foreground">City</label>
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className="mt-0.5" data-testid="picker-new-address-city-input" />
        </div>
        <div>
          <label className="text-[11px] font-medium text-muted-foreground">State</label>
          <select
            value={stateCode}
            onChange={(e) => setStateCode(e.target.value)}
            className="mt-0.5 w-full h-9 px-2.5 rounded-md border border-input bg-background text-sm"
            data-testid="picker-new-address-state-input"
          >
            <option value="">—</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[11px] font-medium text-muted-foreground">Zip</label>
        <Input value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="Zip code" className="mt-0.5" maxLength={10} data-testid="picker-new-address-zip-input" />
      </div>
      <div className="flex items-center gap-2 pt-2">
        <Button variant="ghost" size="sm" onClick={onCancel} className="flex-1" data-testid="picker-new-address-cancel-btn" disabled={saving}>
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
        </Button>
        <Button size="sm" onClick={handleSave} className="flex-1" data-testid="picker-new-address-save-btn" disabled={saving || !address.trim()}>
          {saving ? "Saving…" : <><Check className="w-3.5 h-3.5 mr-1" /> Save & Use</>}
        </Button>
      </div>
    </div>
  );
};

const CustomerAddressPicker = ({ customer, open, onClose, onPick }) => {
  const [addresses, setAddresses] = useState([]);
  const [mode, setMode] = useState("list"); // "list" | "add"
  const [loaded, setLoaded] = useState(false);
  // Stash the latest onPick/onClose in refs so a parent that doesn't
  // memoize these callbacks (the common case) doesn't churn the
  // fetchAddresses useCallback identity → no infinite useEffect loop.
  const onPickRef = useRef(onPick);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onPickRef.current = onPick; }, [onPick]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  // Single-flight guard: don't fire a second GET while the first is in
  // flight. Without this, a parent rerender between fetch start and fetch
  // resolve could double-fire the network call.
  const inFlightRef = useRef(false);

  // Reset state any time we transition between customers (or close+reopen).
  // Previously this only reset on close — switching from customer A to B
  // while the picker stayed open kept stale `loaded=true` and the new
  // customer's addresses were never fetched.
  useEffect(() => {
    setMode("list");
    setLoaded(false);
    setAddresses([]);
    inFlightRef.current = false;
  }, [customer?.customer_id]);

  // Lazy-fetch saved addresses when the picker opens. The fetch is
  // intentionally NOT in a useCallback that depends on onPick/onClose —
  // that pattern caused the effect to re-fire on every parent render.
  useEffect(() => {
    if (!open || !customer?.customer_id || loaded || inFlightRef.current) return;
    let cancelled = false;
    inFlightRef.current = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/customers/${customer.customer_id}/addresses`,
          { headers: getAuthHeaders() }
        );
        if (cancelled) return;
        const addrs = Array.isArray(r.data?.addresses) ? r.data.addresses : [];
        setAddresses(addrs);
        setLoaded(true);
        // 1b: single-address auto-pick — skip the modal entirely.
        if (addrs.length === 1) {
          onPickRef.current?.(addrs[0]);
          onCloseRef.current?.();
        } else if (addrs.length === 0) {
          // No saved addresses → jump straight to the add form so the user
          // can capture one without an extra click.
          setMode("add");
        }
      } catch {
        if (!cancelled) {
          setAddresses([]);
          setLoaded(true);
        }
      } finally {
        if (!cancelled) inFlightRef.current = false;
      }
    })();
    return () => { cancelled = true; };
  }, [open, customer?.customer_id, loaded]);

  if (!customer) return null;

  // Defer rendering the actual <Dialog> until we KNOW we have something to
  // show. This prevents the "modal flashes open then closes" UX bug:
  //   • addresses.length === 1 → onPick/onClose fire from the fetch handler.
  //     We must NOT render the dialog in the brief window between
  //     `setShowAddressPicker(true)` in the parent and the fetch resolving;
  //     otherwise the dialog opens for ~200ms then disappears.
  //   • !loaded → fetch in flight; nothing to show yet, so don't render.
  //   • addresses.length === 0 → mode auto-flips to "add"; OK to show.
  //   • addresses.length >= 2 → list view; OK to show.
  const shouldShow = open && loaded && (mode === "add" || addresses.length >= 2);

  return (
    <Dialog open={shouldShow} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[460px] p-0 gap-0" data-testid="customer-address-picker">
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            {mode === "add"
              ? `New Address for ${customer.name || "Customer"}`
              : `Select Address for ${customer.name || "Customer"}`}
          </DialogTitle>
        </DialogHeader>
        {mode === "list" ? (
          <>
            <div className="p-4 pt-0 space-y-2 max-h-[340px] overflow-y-auto">
              {addresses.map((addr) => (
                <button
                  key={addr.address_id}
                  onClick={() => { onPick(addr); onClose(); }}
                  className="w-full text-left p-3 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all"
                  data-testid={`picker-pick-address-${addr.address_id}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                      {addr.label?.toLowerCase() === "home" ? <Home className="w-4 h-4 text-blue-500" /> :
                       addr.label?.toLowerCase() === "office" ? <Building2 className="w-4 h-4 text-purple-500" /> :
                       <MapPin className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-foreground">{addr.label}</span>
                        {addr.is_default && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-medium bg-amber-50 text-amber-600 border border-amber-100">
                            <Star className="w-2 h-2 fill-current" /> Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{addr.full_address || `${addr.address_line1}, ${addr.city}, ${addr.state} ${addr.zip_code}`}</p>
                    </div>
                  </div>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setMode("add")}
                className="w-full text-left p-3 rounded-xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 transition-all flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground"
                data-testid="picker-add-new-address-btn"
              >
                <div className="w-8 h-8 rounded-lg bg-muted/40 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-4 h-4" />
                </div>
                Add New Address
              </button>
            </div>
            <div className="p-4 pt-2 border-t border-border">
              <Button variant="ghost" onClick={onClose} className="w-full text-muted-foreground text-sm" data-testid="picker-skip-btn">
                Skip — use a different address
              </Button>
            </div>
          </>
        ) : (
          <div className="p-4 pt-0">
            <NewAddressForm
              customerId={customer.customer_id}
              onSaved={(saved) => {
                setAddresses(prev => [...prev, saved]);
                onPick(saved);
                onClose();
              }}
              onCancel={() => addresses.length > 0 ? setMode("list") : onClose()}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerAddressPicker;
